import { FormEventHandler, ReactElement, useState } from 'react';
import { User } from '@/api';
import { useResendEmailVerificationAction } from '../actions';
import {
    UIAnimation,
    UIButton,
    UIModal,
    UIModalFooter,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText
} from '@/ui';
import emailInviteSent from '@/common/assets/lotties/email-invite-sent.json';
import { blue, red } from '@/ui/styles';
import { Trans } from 'react-i18next';
import { useMeTranslation } from '../hooks';

type EmailSettingsSectionProps = { user: User };

export function EmailSettingsSection(
    props: EmailSettingsSectionProps
): ReactElement {
    const { user } = props;

    const { t } = useMeTranslation();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const resendEmailVerification = useResendEmailVerificationAction();

    function onSubmit() {
        resendEmailVerification
            .resendEmailVerification()
            .then(() => {
                setShowSuccessModal(true);
            })
            .catch(() => {});
    }

    function onSuccessClose() {
        setShowSuccessModal(false);
    }

    return (
        <>
            <EmailSettingsSectionForm
                email={user.email}
                isVerified={user.emailVerified}
                loading={resendEmailVerification.loading}
                onResendVerification={onSubmit}
            />
            <UIModal isOpen={showSuccessModal} onClose={onSuccessClose}>
                {showSuccessModal && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 32,
                            gap: 16
                        }}
                    >
                        <UIAnimation
                            animationData={emailInviteSent}
                            loop={false}
                            style={{
                                height: 400
                            }}
                        />
                        <UIText variant="h1" textAlign="center">
                            {t('components.email_settings_section.sent_title')}
                        </UIText>
                        <UIText textAlign="center">
                            <Trans
                                ns="me"
                                i18nKey="components.email_settings_section.sent_description"
                                values={{ email: user.email }}
                                components={{
                                    b: <UIText bold type="link" />,
                                    b1: <UIText bold />
                                }}
                            />
                        </UIText>
                    </div>
                )}
                <UIModalFooter
                    actions={[
                        {
                            text: t('shared.labels.done'),
                            variant: 'primary',
                            onClick: onSuccessClose
                        }
                    ]}
                />
            </UIModal>
        </>
    );
}

type EmailSettingsSectionFormProps = {
    email: string;
    isVerified: boolean;
    loading: boolean;
    onResendVerification: () => void;
};
export function EmailSettingsSectionForm(
    props: EmailSettingsSectionFormProps
): ReactElement {
    const { email, isVerified, loading, onResendVerification } = props;

    const { t } = useMeTranslation();

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        onResendVerification?.();
    };

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t('components.email_settings_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 8
                            }}
                        >
                            <UIText>{email}</UIText>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isVerified ? blue : red,
                                    borderRadius: 999,
                                    paddingLeft: 8,
                                    paddingTop: 4,
                                    paddingRight: 8,
                                    paddingBottom: 4
                                }}
                            >
                                <UIText type="white" variant="small" medium>
                                    {isVerified
                                        ? t(
                                              'components.email_settings_section.verified'
                                          )
                                        : t(
                                              'components.email_settings_section.not_verified'
                                          )}
                                </UIText>
                            </div>
                        </div>

                        {!isVerified && (
                            <UIButton
                                variant="textPrimary"
                                type="submit"
                                loading={loading}
                            >
                                {t(
                                    'components.email_settings_section.send_verification'
                                )}
                            </UIButton>
                        )}
                    </div>
                </UISectionContent>
            </form>
        </UISection>
    );
}
